import { handleError } from '@anchorage/sentry';
import { isValid } from 'date-fns/isValid';
import { parse } from 'date-fns/parse';
import { parseISO } from 'date-fns/parseISO';

import { getUTCDateMillis } from './getUTCDateMillis';
import type { DateOptions } from './types';

/**
 * Parses a date from either a unix nanosecond timestamp or an ISO 8601 string
 * (e.g. from GraphQL Time scalars).
 * @param date unix nanosecond timestamp or ISO 8601 string
 * @param opts
 */
export function parseDate(date: string | number, opts?: DateOptions): Date {
  if (typeof date === 'string') {
    // Handle ISO 8601 strings (e.g. "2024-02-13T17:45:12.801682Z" from GraphQL Time scalars)
    // and date-only strings (e.g. "2024-02-13", "2024-2-13" from GraphQL Date scalars)
    if (/^\d{4}-\d{1,2}-\d{1,2}(T|$)/.test(date)) {
      const isDateOnly = !date.includes('T');
      // Normalize non-zero-padded date-only strings (e.g. "2024-2-3" → "2024-02-03")
      // so parseISO can handle them — Go's Date scalar uses format "2006-1-2"
      const normalized = date.replace(
        /^(\d{4})-(\d{1,2})-(\d{1,2})/,
        (_, y, m, d) => `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`,
      );
      const parsedDate = parseISO(normalized);
      if (isValid(parsedDate)) {
        // Date-only strings are calendar dates without timezone info — skip the
        // UTC adjustment since shifting local midnight would corrupt the date.
        if (!isDateOnly && opts && opts.formatInUTC) {
          const millis = getUTCDateMillis(parsedDate.getTime());
          return new Date(millis);
        }
        return parsedDate;
      }
    }
  }

  // Cast to a number. All of our dates are in unix nanos
  const timestampNanos = +date;
  // Cast to milliseconds so that we can use date fns
  const timestampMillis = Math.round(timestampNanos / 1000000);

  let millis = timestampMillis;
  // If we should format this date in UTC instead of local time, add/subtract
  // the appropriate offset
  if (opts && opts.formatInUTC) {
    millis = getUTCDateMillis(timestampMillis);
  }

  // Parse the date from the timestamp milliseconds format relative to now
  const parsedDate = parse(millis.toString(), 'T', new Date());
  if (!isValid(parsedDate)) {
    handleError(new Error(`Unsupported date format: ${date}`));
  }
  return parsedDate;
}
